import { atomField, getFormIsInvalidAtom, required } from 'atoms/form-atoms';
import {
  Dropdown,
  Form,
  InlineNotification,
  Modal,
  TextInput,
} from '@carbon/react';
import { atom, useAtom, useAtomValue } from 'jotai';
import { createResourceAtom } from 'lib/atoms';
import { formatResource } from 'utils/misc';
import {
  getUniqueBasinNames,
  getSelectedIntervalId,
} from 'pages/structure-grids/util';
import React, { useEffect, useMemo, useState } from 'react';
import { getBasinIntervals } from '../add-structure-grid/utils';
import styles from './edit-structure-grid-modal.module.scss';
import { updateStructureGridAtom } from '../atoms';
import { UpdateStructureGridPayload } from 'types/api-payloads';
import { GeologicIntervalResponse } from 'types/api-responses';

export interface StructureGridEditData {
  id: number;
  gridSurface: string;
  basinName: string;
  geologicInterval: string;
  owner: string;
  source: string;
}

interface EditStructureGridModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  initialData?: StructureGridEditData;
}

// create form structure for edit structure grid
const createEditStructureGridForm = (initialData?: StructureGridEditData) => {
  return {
    gridSurface: atomField<string>(initialData?.gridSurface || '', {
      validate: (v) => required(v, 'Surface Name is required'),
    }),
    basinName: atomField<string>(initialData?.basinName || ''),
    geologicInterval: atomField<string>(initialData?.geologicInterval || '', {
      validate: (v) => required(v, 'Geologic Interval is required'),
    }),
    owner: atomField<string>(initialData?.owner || '', {
      validate: (v) => required(v, 'Data Owner is required'),
    }),
    source: atomField<string>(initialData?.source || '', {
      validate: (v) => required(v, 'Data Source is required'),
    }),
  };
};

export const EditStructureGridModal: React.FC<EditStructureGridModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
}) => {
  const [editState, editStructureGrid] = useAtom(updateStructureGridAtom);
  const geoLogicalIntervalsAtom = useMemo(
    () => createResourceAtom<GeologicIntervalResponse>('/geologic_intervals'),
    []
  );
  const geoLogicalIntervalsData = useAtomValue(geoLogicalIntervalsAtom);
  const { data: geologicalIntervalsData } = formatResource(
    geoLogicalIntervalsData
  );
  const basins = getUniqueBasinNames(
    geologicalIntervalsData ?? { geologicIntervals: [], meta: { count: 0 } }
  );

  const form = useMemo(
    () => atom(createEditStructureGridForm(initialData)),
    [initialData]
  );
  const formValues = useAtomValue(form);

  const invalidFormAtom = useMemo(() => {
    return getFormIsInvalidAtom(form);
  }, [form]);

  const isFormInvalid = useAtomValue(invalidFormAtom);

  // Form field atoms
  const [gridSurface, setGridSurface] = useAtom(formValues.gridSurface);
  const [basinName, setBasinName] = useAtom(formValues.basinName);
  const [geologicInterval, setGeologicInterval] = useAtom(
    formValues.geologicInterval
  );
  const [owner, setOwner] = useAtom(formValues.owner);
  const [source, setSource] = useAtom(formValues.source);
  const [editError, setEditError] = useState<string | null>(null);

  // computing geologic intervals for the dropdown
  const { intervalItemsNames, intervalItems } = useMemo(() => {
    if (!geologicalIntervalsData)
      return { intervalItemsNames: [], intervalItems: [] };

    const uniqueBasins = getUniqueBasinNames(geologicalIntervalsData);
    const geologicIntervals = getBasinIntervals(
      geologicalIntervalsData,
      uniqueBasins
    );

    // Get intervals for selected basin
    const intervals =
      basinName.value && geologicIntervals[basinName.value]
        ? geologicIntervals[basinName.value]
        : [];

    return {
      intervalItemsNames: intervals.map(
        (interval) => interval.geologicInterval
      ),
      intervalItems: intervals, // Keep full objects for ID lookup
    };
  }, [geologicalIntervalsData, basinName.value]);

  // Reset geologic interval when basin changes
  useEffect(() => {
    if (
      initialData &&
      basinName.value !== initialData.basinName &&
      basinName.value !== ''
    ) {
      setGeologicInterval('');
    }
  }, [basinName.value, setGeologicInterval, initialData]);

  const handleSave = () => {
    if (isFormInvalid || !initialData) return;

    const intervalId = getSelectedIntervalId(
      geologicInterval.value,
      intervalItems
    );
    if (!intervalId) {
      return;
    }

    const updateData: UpdateStructureGridPayload = {
      source: source.value,
      gridSurface: gridSurface.value,
      owner: owner.value,
      geologicIntervalId: intervalId,
    };

    editStructureGrid({
      url: `/structure_grids/${initialData.id}`,
      type: 'PATCH',
      data: updateData,
      onSuccess: () => {
        onSave();
        setEditError(null);
        onClose();
      },
      onError: (error) => {
        setEditError(
          (error as { message: string })?.message ||
            'Error saving structure grid'
        );
      },
    });
  };

  const handleClose = () => {
    if (!editState.loading) {
      setEditError(null);
      onClose();
    }
  };

  return (
    <Modal
      open={isOpen}
      onRequestClose={handleClose}
      modalHeading="Edit Data"
      primaryButtonText="Save"
      secondaryButtonText="Cancel"
      onRequestSubmit={handleSave}
      onSecondarySubmit={handleClose}
      primaryButtonDisabled={Boolean(isFormInvalid) || editState.loading}
      preventCloseOnClickOutside={editState.loading}
      size="sm"
      className={styles.editModal}
    >
      <Form>
        {editError && <InlineNotification kind="error" title={editError} />}
        <TextInput
          id="gridSurface"
          labelText="Surface Name"
          value={gridSurface.value}
          onChange={(e) => setGridSurface(e.target.value)}
          invalid={!gridSurface.isValid}
          invalidText={!gridSurface.isValid ? gridSurface.error : ''}
          disabled={editState.loading}
        />

        <div className={styles.fieldRow}>
          <Dropdown
            id="basinName"
            titleText="Basin"
            label="Select Basin"
            items={basins}
            selectedItem={basinName.value}
            onChange={({ selectedItem }) => setBasinName(selectedItem || '')}
            disabled={editState.loading}
          />

          <Dropdown
            id="geologicInterval"
            titleText="Geologic Interval"
            label="Select Geologic Interval"
            items={intervalItemsNames}
            selectedItem={geologicInterval.value}
            onChange={({ selectedItem }) => {
              setGeologicInterval(selectedItem || '');
            }}
            invalid={!geologicInterval.isValid}
            invalidText={
              !geologicInterval.isValid ? geologicInterval.error : ''
            }
            disabled={editState.loading || !basinName.value}
          />
        </div>

        <div className={styles.fieldRow}>
          <TextInput
            id="owner"
            labelText="Data Owner"
            value={owner.value}
            onChange={(e) => setOwner(e.target.value)}
            invalid={!owner.isValid}
            invalidText={!owner.isValid ? owner.error : ''}
            disabled={editState.loading}
          />

          <TextInput
            id="source"
            labelText="Data Source"
            value={source.value}
            onChange={(e) => setSource(e.target.value)}
            invalid={!source.isValid}
            invalidText={!source.isValid ? source.error : ''}
            disabled={editState.loading}
          />
        </div>
      </Form>
    </Modal>
  );
};
